import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import {
  Add,
  FolderOpenOutlined,
  Refresh,
  Search,
  ViewColumnOutlined,
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';

import { useGetAllCategory } from '../../api-http/categories';
import { DialogModal } from '../../components';
import Breadcrumbs from '../../components/Breadcrumb';
import { DEBOUNCE_TIME } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useHandleSearch } from '../../util/useHandleSearch';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useLocationSearch } from '../../util/useLocationSearch';
import { PresentationForm } from './PresentationForm';
import { PresentationTable } from './PresentationTable';

export interface PresentationColumns {
  category: boolean;
  created: boolean;
  updatedBy: boolean;
}

const controlStyles = {
  height: 40,
  borderRadius: 2,
  textTransform: 'none',
};

const PresentationPage: FC = () => {
  metaTitle('Presentations');
  const location = useLocation();
  const searchVal = useLocationSearch();
  const [open, setOpen] = React.useState(false);
  const { data: categoryData, refetch } = useGetAllCategory();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [search, setSearch] = useState(searchVal || '');
  const [searchedPresentation, setSearchedPresentation] = useState<string>(
    searchVal || '',
  );
  const [refresh, setRefresh] = useState(false);
  const [columnAnchor, setColumnAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<PresentationColumns>({
    category: false,
    created: false,
    updatedBy: false,
  });
  useHandleSearch(search);

  const { switchedOrgId } = useAuth();

  useEffect(() => {
    refetch();
  }, [switchedOrgId]);

  const disableAddNew = useIsUserRole([UserRole.user, UserRole.dataEditor]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'Presentations',
            to: `${location?.pathname}${location?.search}`,
            description:
              'This page houses all created presentations, allowing users to build presentations using existing slides from the Slides page. Users can search and filter presentations based on assigned categories. They can also view the latest update details, including time, date, and person.',
          },
        ]}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{ fontSize: 28, fontWeight: 700, color: '#0f172a', mb: 0.5 }}
          >
            Presentations
          </Typography>
          <Typography sx={{ color: '#64748b' }}>
            Create, organize, and manage content for your displays.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={disableAddNew}
            sx={{ ...controlStyles, bgcolor: '#0b8fca', fontWeight: 700 }}
          >
            New presentation
          </Button>
          <NavLink to="/categories" style={{ textDecoration: 'none' }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpenOutlined />}
              sx={{ ...controlStyles, fontWeight: 600 }}
            >
              Manage categories
            </Button>
          </NavLink>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        }}
      >
        <TextField
          placeholder="Search presentations"
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            debounce(
              () => setSearchedPresentation(event.target.value),
              DEBOUNCE_TIME,
            )();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', lg: 360 },
            '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
          }}
        />

        <Stack
          direction="row"
          spacing={1.25}
          sx={{ flexWrap: 'wrap', rowGap: 1.25 }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: 180, sm: 220 } }}>
            <Select
              displayEmpty
              value={selectedCategory}
              onChange={(event: SelectChangeEvent<string>) =>
                setSelectedCategory(event.target.value)
              }
              sx={{ height: 40, borderRadius: 2 }}
              inputProps={{ 'aria-label': 'Filter by category' }}
            >
              <MenuItem value="">All categories</MenuItem>
              {categoryData?.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<ViewColumnOutlined />}
            onClick={(event) => setColumnAnchor(event.currentTarget)}
            sx={{ ...controlStyles, color: '#334155', borderColor: '#cbd5e1' }}
          >
            Columns
          </Button>
          <Menu
            anchorEl={columnAnchor}
            open={Boolean(columnAnchor)}
            onClose={() => setColumnAnchor(null)}
          >
            <MenuItem
              onClick={() =>
                setVisibleColumns((value) => ({
                  ...value,
                  category: !value.category,
                }))
              }
            >
              <Checkbox checked={visibleColumns.category} size="small" />
              Category
            </MenuItem>
            <MenuItem
              onClick={() =>
                setVisibleColumns((value) => ({
                  ...value,
                  updatedBy: !value.updatedBy,
                }))
              }
            >
              <Checkbox checked={visibleColumns.updatedBy} size="small" />
              Updated by
            </MenuItem>
            <MenuItem
              onClick={() =>
                setVisibleColumns((value) => ({
                  ...value,
                  created: !value.created,
                }))
              }
            >
              <Checkbox checked={visibleColumns.created} size="small" />
              Created
            </MenuItem>
          </Menu>
          <IconButton
            aria-label="Refresh presentations"
            onClick={() => setRefresh(!refresh)}
            sx={{
              width: 40,
              height: 40,
              border: '1px solid #cbd5e1',
              borderRadius: 2,
              color: '#2563eb',
            }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <PresentationTable
        selectedCategory={selectedCategory}
        searchedPresentation={searchedPresentation}
        onRefresh={refresh}
        visibleColumns={visibleColumns}
      />

      <DialogModal
        title="New presentation"
        onClose={() => setOpen(false)}
        open={open}
        width={520}
        style={{
          maxWidth: 'calc(100vw - 32px)',
          padding: '28px',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
        }}
      >
        <PresentationForm onFinished={() => setOpen(false)} />
      </DialogModal>
    </Box>
  );
};

export default PresentationPage;
